import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlaidLink } from 'react-plaid-link';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, 
  Plus, 
  Trash2, 
  Star,
  ArrowDownToLine,
  ArrowUpFromLine,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  DollarSign,
  CreditCard,
  Shield,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import PlaidConsentModal from './PlaidConsentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankAccountManager() {
  const [accounts, setAccounts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [isMockMode, setIsMockMode] = useState(false);
  
  // Transfer dialog state
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferType, setTransferType] = useState('deposit');
  const [transferAmount, setTransferAmount] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [processing, setProcessing] = useState(false);

  // Plaid consent + delete-my-data state (B2, B3)
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [deleteDataDialog, setDeleteDataDialog] = useState(false);
  const [deletingData, setDeletingData] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accountsRes, transfersRes, limitsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bank/accounts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/bank/transfers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/bank/limits`, { withCredentials: true })
      ]);
      
      setAccounts(accountsRes.data.accounts || []);
      setTransfers(transfersRes.data.transfers || []);
      setLimits(limitsRes.data);
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Plaid Link onSuccess callback - exchange public_token for access_token
  const onPlaidSuccess = useCallback(async (public_token, metadata) => {
    try {
      const linkRes = await axios.post(
        `${BACKEND_URL}/api/bank/link`,
        { public_token },
        { withCredentials: true }
      );
      const count = linkRes.data?.accounts?.length || 1;
      const inst = linkRes.data?.institution_name || metadata?.institution?.name || 'your bank';
      toast.success(`Linked ${count} account${count > 1 ? 's' : ''} from ${inst}`);
      setLinkToken(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link account');
    } finally {
      setLinking(false);
    }
  }, []);

  const onPlaidExit = useCallback((err, metadata) => {
    setLinking(false);
    setLinkToken(null);
    if (err) {
      toast.error(err.display_message || err.error_message || 'Bank linking cancelled');
    }
  }, []);

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
  });

  // Auto-open Plaid Link as soon as token is ready
  useEffect(() => {
    if (linkToken && plaidReady && !isMockMode) {
      openPlaidLink();
    }
  }, [linkToken, plaidReady, isMockMode, openPlaidLink]);

  const handleLinkAccount = async () => {
    // B2: Show explicit Plaid consent screen before doing anything.
    setConsentOpen(true);
  };

  const performLinkAccount = async () => {
    setLinking(true);
    try {
      const tokenRes = await axios.get(`${BACKEND_URL}/api/bank/link-token`, {
        withCredentials: true
      });
      
      if (tokenRes.data.is_mock) {
        // Development mode - create mock link
        setIsMockMode(true);
        const linkRes = await axios.post(
          `${BACKEND_URL}/api/bank/link`,
          { public_token: 'mock_public_token' },
          { withCredentials: true }
        );
        toast.success('Development: Mock bank account linked!');
        setLinking(false);
        loadData();
        return;
      }
      
      // Real Plaid - set token to trigger usePlaidLink to open
      setIsMockMode(false);
      setLinkToken(tokenRes.data.link_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link account');
      setLinking(false);
    }
  };

  const handleConsentAccept = async () => {
    setConsentLoading(true);
    try {
      // Persist the consent for the audit trail (B2 backend hook).
      await axios.post(
        `${BACKEND_URL}/api/bank/consent`,
        { consent_version: '1.0' },
        { withCredentials: true }
      );
      setConsentOpen(false);
      await performLinkAccount();
    } catch (error) {
      toast.error('Could not record consent. Please try again.');
    } finally {
      setConsentLoading(false);
    }
  };

  // B8: Reconnect (Plaid Link update mode) for items in needs_reauth state.
  const handleReconnect = async (accountId) => {
    setLinking(true);
    try {
      const tokenRes = await axios.post(
        `${BACKEND_URL}/api/bank/accounts/${accountId}/reconnect-link-token`,
        {},
        { withCredentials: true }
      );
      setIsMockMode(false);
      setLinkToken(tokenRes.data.link_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start reconnect');
      setLinking(false);
    }
  };

  // B3: Delete-my-data
  const handleDeleteMyData = async () => {
    setDeletingData(true);
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/users/me/data`, {
        withCredentials: true,
      });
      const items = res.data?.summary?.plaid_items_revoked ?? 0;
      toast.success(`Your data has been deleted. ${items} bank connection(s) revoked.`);
      setDeleteDataDialog(false);
      // Force logout after wipe
      setTimeout(() => { window.location.href = '/'; }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete data');
    } finally {
      setDeletingData(false);
    }
  };

  const handleUnlinkAccount = async (accountId) => {
    if (!confirm('Are you sure you want to unlink this bank account?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/bank/accounts/${accountId}`, {
        withCredentials: true
      });
      toast.success('Bank account unlinked');
      loadData();
    } catch (error) {
      toast.error('Failed to unlink account');
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/bank/accounts/${accountId}/primary`, {}, {
        withCredentials: true
      });
      toast.success('Primary account updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update primary account');
    }
  };

  const handleTransfer = async () => {
    if (!selectedAccount || !transferAmount || parseFloat(transferAmount) <= 0) {
      toast.error('Please select an account and enter a valid amount');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/bank/transfer`,
        {
          linked_account_id: selectedAccount,
          transfer_type: transferType,
          amount: parseFloat(transferAmount)
        },
        { withCredentials: true }
      );

      toast.success(response.data.message);
      setTransferDialog(false);
      setTransferAmount('');
      setSelectedAccount('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-700"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="border-stone-200">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-stone-200" data-testid="bank-account-manager">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-indigo-600" />
                Bank Accounts
              </CardTitle>
              <CardDescription>
                Connect your bank for deposits and withdrawals
              </CardDescription>
            </div>
            <Button
              onClick={handleLinkAccount}
              disabled={linking}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="link-bank-btn"
            >
              {linking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Link Bank
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="accounts" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="accounts">Linked Accounts</TabsTrigger>
              <TabsTrigger value="transfers">Transfer History</TabsTrigger>
            </TabsList>

            <TabsContent value="accounts">
              {accounts.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Bank Accounts Linked</h3>
                  <p className="text-stone-500 mb-6">
                    Link your bank account to deposit and withdraw funds
                  </p>
                  <Button
                    onClick={handleLinkAccount}
                    disabled={linking}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Link Your First Account
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {accounts.map((account) => (
                    <motion.div
                      key={account.account_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl border border-stone-200 bg-white hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mr-4">
                            <Building2 className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <div className="flex items-center">
                              <span className="font-semibold text-stone-900">
                                {account.institution_name}
                              </span>
                              {account.is_primary && (
                                <Badge className="ml-2 bg-amber-100 text-amber-700">
                                  <Star className="w-3 h-3 mr-1" />
                                  Primary
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-stone-500">
                              {account.account_name} •••• {account.account_mask}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {(account.needs_reauth || account.status === 'needs_reauth' || account.pending_expiration) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReconnect(account.account_id)}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              data-testid={`reconnect-${account.account_id}`}
                            >
                              <AlertCircle className="w-4 h-4 mr-1" />
                              Reconnect
                            </Button>
                          )}
                          {!account.is_primary && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSetPrimary(account.account_id)}
                              data-testid={`set-primary-${account.account_id}`}
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnlinkAccount(account.account_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`unlink-${account.account_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <Button
                      onClick={() => {
                        setTransferType('deposit');
                        setTransferDialog(true);
                      }}
                      className="bg-green-600 hover:bg-green-700 py-6"
                      data-testid="deposit-btn"
                    >
                      <ArrowDownToLine className="w-5 h-5 mr-2" />
                      Deposit to Mini Bank
                    </Button>
                    <Button
                      onClick={() => {
                        setTransferType('withdrawal');
                        setTransferDialog(true);
                      }}
                      variant="outline"
                      className="py-6 border-indigo-200 hover:bg-indigo-50"
                      data-testid="withdraw-btn"
                    >
                      <ArrowUpFromLine className="w-5 h-5 mr-2" />
                      Withdraw to Bank
                    </Button>
                  </div>

                  {/* Daily Limits */}
                  {limits && (
                    <div className="mt-6 p-4 bg-stone-50 rounded-xl">
                      <h4 className="font-medium text-stone-900 mb-3">Daily Limits</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-stone-500 mb-1">Withdrawals Today</div>
                          <div className="text-lg font-semibold text-stone-900">
                            ${limits.today_usage?.withdrawals?.toFixed(2) || '0.00'}
                            <span className="text-sm font-normal text-stone-500">
                              {' '}/ ${limits.limits?.max_daily_withdrawal?.toFixed(2)}
                            </span>
                          </div>
                          <Progress 
                            value={(limits.today_usage?.withdrawals / limits.limits?.max_daily_withdrawal) * 100 || 0} 
                            className="h-2 mt-2"
                          />
                        </div>
                        <div>
                          <div className="text-sm text-stone-500 mb-1">Deposits Today</div>
                          <div className="text-lg font-semibold text-stone-900">
                            ${limits.today_usage?.deposits?.toFixed(2) || '0.00'}
                            <span className="text-sm font-normal text-stone-500">
                              {' '}/ ${limits.limits?.max_daily_deposit?.toFixed(2)}
                            </span>
                          </div>
                          <Progress 
                            value={(limits.today_usage?.deposits / limits.limits?.max_daily_deposit) * 100 || 0} 
                            className="h-2 mt-2"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Danger zone — right-to-delete (B3) */}
                  <div className="mt-6 pt-6 border-t border-dashed border-stone-200">
                    <div className="flex items-center justify-between bg-rose-50/50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-rose-900 text-sm">Delete all my data</div>
                          <div className="text-xs text-rose-700/80 mt-0.5 max-w-md">
                            Revokes every Plaid connection, wipes your transactions and profile, and logs you out. Cannot be undone.
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteDataDialog(true)}
                        className="border-rose-300 text-rose-700 hover:bg-rose-100"
                        data-testid="delete-my-data-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="transfers">
              {transfers.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Transfer History</h3>
                  <p className="text-stone-500">
                    Your deposits and withdrawals will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transfers.map((transfer) => (
                    <motion.div
                      key={transfer.transfer_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 rounded-lg border border-stone-200 bg-white"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                            transfer.transfer_type === 'deposit' 
                              ? 'bg-green-100' 
                              : 'bg-indigo-100'
                          }`}>
                            {transfer.transfer_type === 'deposit' ? (
                              <ArrowDownToLine className="w-5 h-5 text-green-600" />
                            ) : (
                              <ArrowUpFromLine className="w-5 h-5 text-indigo-600" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-stone-900">
                              {transfer.transfer_type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                            </div>
                            <div className="text-sm text-stone-500">
                              {transfer.account_info?.institution_name} •••• {transfer.account_info?.account_mask}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className={`text-lg font-semibold ${
                            transfer.transfer_type === 'deposit' 
                              ? 'text-green-600' 
                              : 'text-stone-900'
                          }`}>
                            {transfer.transfer_type === 'deposit' ? '+' : '-'}${transfer.amount.toFixed(2)}
                          </div>
                          {getStatusBadge(transfer.status)}
                        </div>
                      </div>
                      
                      {transfer.hold_until && transfer.status === 'pending' && (
                        <div className="mt-3 p-2 bg-amber-50 rounded-lg text-sm text-amber-800 flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          24-hour hold until {formatDate(transfer.hold_until)}
                        </div>
                      )}
                      
                      <div className="mt-2 text-xs text-stone-400">
                        {formatDate(transfer.created_at)}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              {transferType === 'deposit' ? (
                <ArrowDownToLine className="w-5 h-5 mr-2 text-green-600" />
              ) : (
                <ArrowUpFromLine className="w-5 h-5 mr-2 text-indigo-600" />
              )}
              {transferType === 'deposit' ? 'Deposit to Mini Bank' : 'Withdraw to Bank'}
            </DialogTitle>
            <DialogDescription>
              {transferType === 'deposit' 
                ? 'Transfer funds from your bank to Mini Bank (24-hour hold applies)'
                : 'Transfer funds from Mini Bank to your bank account'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Account Selection */}
            <div className="space-y-2">
              <Label>Select Bank Account</Label>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger data-testid="account-select">
                  <SelectValue placeholder="Choose account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.account_id}>
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 mr-2 text-stone-400" />
                        {account.institution_name} •••• {account.account_mask}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>Amount ({transferType === 'withdrawal' ? 'Min $10' : 'Min $1'})</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  type="number"
                  step="0.01"
                  min={transferType === 'withdrawal' ? '10' : '1'}
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                  data-testid="transfer-amount-input"
                />
              </div>
            </div>

            {/* Limits Info */}
            {limits && (
              <div className="p-3 bg-stone-50 rounded-lg text-sm">
                <div className="flex justify-between text-stone-600">
                  <span>Daily {transferType} limit:</span>
                  <span className="font-medium">
                    ${transferType === 'withdrawal' 
                      ? limits.limits?.max_daily_withdrawal?.toFixed(2)
                      : limits.limits?.max_daily_deposit?.toFixed(2)
                    }
                  </span>
                </div>
                <div className="flex justify-between text-stone-600 mt-1">
                  <span>Remaining today:</span>
                  <span className="font-medium text-green-600">
                    ${transferType === 'withdrawal'
                      ? limits.today_usage?.withdrawal_remaining?.toFixed(2)
                      : limits.today_usage?.deposit_remaining?.toFixed(2)
                    }
                  </span>
                </div>
              </div>
            )}

            {/* 24-hour hold warning for deposits */}
            {transferType === 'deposit' && (
              <div className="p-3 bg-amber-50 rounded-lg flex items-start text-sm text-amber-800">
                <Clock className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>24-Hour Hold:</strong> Deposits are held for 24 hours before becoming available for withdrawals or contributions.
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleTransfer}
              disabled={processing || !selectedAccount || !transferAmount}
              className={transferType === 'deposit' ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}
              data-testid="confirm-transfer-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {transferType === 'deposit' ? 'Deposit' : 'Withdraw'} ${transferAmount || '0.00'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plaid consent modal (B2) */}
      <PlaidConsentModal
        open={consentOpen}
        loading={consentLoading}
        onAccept={handleConsentAccept}
        onDecline={() => setConsentOpen(false)}
      />

      {/* Right-to-delete confirmation (B3) */}
      <Dialog open={deleteDataDialog} onOpenChange={setDeleteDataDialog}>
        <DialogContent data-testid="delete-data-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center text-rose-700">
              <Trash2 className="w-5 h-5 mr-2" />
              Permanently delete your data
            </DialogTitle>
            <DialogDescription>
              This revokes every Plaid bank connection, erases your transactions and profile, and logs you out. It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-900">
            To confirm, click <strong>Yes, delete everything</strong>. You'll be redirected to the homepage.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDataDialog(false)} disabled={deletingData}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteMyData}
              disabled={deletingData}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-data-btn"
            >
              {deletingData ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                'Yes, delete everything'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
